// The Topics & Subtopics widgets serve three products. Social Listening counts
// mentions, one topic each; Review Analysis counts reviews, and a review may
// mention several topics, so its shares are "of reviews" and can pass 100%;
// Content Analysis counts posts (by caption) and comments in two tables.
export interface TopicsCopy {
  emptyNoun: string
  noun: string
  // Singular of `noun`, for a count of exactly one.
  nounSingular: string
  // Label on a card's link when the source gives no domain to show.
  openLabel: string
  relatedEmptyHint: string
  relatedHintSubtopic: string
  relatedHintTopic: string
  relatedTitle: string
  shareOfTotal: string
  subtitle: string
  // The topic modal's summary line in body text instead of the widget violet.
  summaryNeutral?: boolean
  // Section heading, when the host draws more than one table.
  title?: string
  // The sentiment hover card's heading; "Total Mentions" when unset.
  totalLabel?: string
}

export const SOCIAL_TOPICS_COPY: TopicsCopy = {
  emptyNoun: 'mentions',
  noun: 'mentions',
  nounSingular: 'mention',
  openLabel: 'Open post',
  relatedEmptyHint: 'Posts appear here once mentions are matched to it.',
  relatedHintSubtopic: 'Real posts behind this subtopic',
  relatedHintTopic: 'Real posts behind this topic',
  relatedTitle: 'Related Mentions',
  shareOfTotal: 'of total',
  subtitle: 'What people are talking about across your collected mentions',
}

export const REVIEW_TOPICS_COPY: TopicsCopy = {
  emptyNoun: 'reviews',
  noun: 'reviews',
  nounSingular: 'review',
  openLabel: 'Open review',
  relatedEmptyHint: 'Reviews appear here once they are matched to it.',
  relatedHintSubtopic: 'Real reviews behind this subtopic',
  relatedHintTopic: 'Real reviews behind this topic',
  relatedTitle: 'Related Reviews',
  shareOfTotal: 'of reviews',
  subtitle: 'What guests are talking about across your collected reviews',
  summaryNeutral: true,
}

// Content Analysis: one table by post (caption sentiment) and one by comment.
export const CONTENT_TOPICS_COPY: Record<'comments' | 'posts', TopicsCopy> = {
  comments: {
    emptyNoun: 'comments',
    noun: 'comments',
    nounSingular: 'comment',
    openLabel: 'Open comment',
    relatedEmptyHint: 'Comments appear here once they are matched to it.',
    relatedHintSubtopic: 'Real comments behind this subtopic',
    relatedHintTopic: 'Real comments behind this topic',
    relatedTitle: 'Related Comments',
    shareOfTotal: 'of total',
    subtitle:
      'Understand the questions, opinions, and concerns shared in comments',
    title: 'Topics & Subtopics by Comments',
    totalLabel: 'Total Comments',
  },
  posts: {
    emptyNoun: 'posts',
    noun: 'posts',
    nounSingular: 'post',
    openLabel: 'Open post',
    relatedEmptyHint: 'Posts appear here once they are matched to it.',
    relatedHintSubtopic: 'Real posts behind this subtopic',
    relatedHintTopic: 'Real posts behind this topic',
    relatedTitle: 'Related Posts',
    shareOfTotal: 'of total',
    subtitle: 'Explore the key themes and topics in the posts',
    title: 'Topics & Subtopics by Posts',
    totalLabel: 'Total Posts',
  },
}

/** "1 mention", "2 mentions", "1,240 mentions". */
export const countLabel = (
  count: number,
  copy: Pick<TopicsCopy, 'noun' | 'nounSingular'>
): string =>
  `${count.toLocaleString()} ${count === 1 ? copy.nounSingular : copy.noun}`
